import React, { useRef, useState } from "react";

// SwipeActionRow — generic swipe-to-reveal quick actions for mobile list rows.
//
// R5 discovery (DECISIONS.md R5-Discovery): the platform's only existing
// row-action swipe lived inline in the inbox channel row
// (inbox/CustomChannelPreview.js D24 swipe-to-archive — raw touch handlers,
// deltaX clamp, half-threshold snap, translateX reveal). That pattern is
// extracted here and generalized instead of growing a second inline copy:
// props-driven actions, an axis lock so horizontal swipes never fight
// vertical list scrolling, and the same half-threshold open/close snap.
// Club Radar mobile rows (frame 1e) are the first consumer.
//
// Purely presentational and consumer-agnostic (no radar/inbox imports):
// consumers pass the SAME handlers their row ⋯ menus already use — swipe is
// an accelerator, never the only path. Desktop/keyboard/assistive tech keep
// the ⋯ path: while closed the revealed buttons are aria-hidden and out of
// the tab order.
//
// Props:
//   actions      [{ key, label, icon?, onAction(), destructive?, background?,
//                   ariaLabel? }] — revealed right-to-left behind the row.
//                 destructive → danger red, otherwise brand blue (or an
//                 explicit `background`). Empty array → children render as-is.
//   actionWidth  px width per revealed action (default 74 per the 1e artboard;
//                 every target is ≥44px in both dimensions).
//   disabled     render children only (no gesture, no panel).
//   style        outer container (row divider border etc. live here so the
//                 revealed panel sits under the same hairline).
//   contentStyle extra styles for the sliding foreground.
//
// Gesture contract (helpers exported for unit tests):
//   • axis lock — the first AXIS_LOCK_PX of movement decide the axis once;
//     vertical intent cancels the swipe for the rest of the touch (native
//     scrolling continues — the foreground also sets `touch-action: pan-y`
//     so the browser keeps vertical panning on long lists), horizontal
//     intent drags the row.
//   • clamp — the row only travels between 0 and −(revealed width).
//   • settle — released past half the reveal → open, otherwise snap shut.
//   • a tap on the foreground while open closes the row and swallows the
//     click, so the row's own onClick (e.g. open details) never misfires.

export const AXIS_LOCK_PX = 10;
export const DEFAULT_ACTION_WIDTH = 74;

// Decide the gesture axis after either delta passes the lock threshold;
// below the threshold the axis is still undecided (null).
export const resolveAxis = (dx, dy, lockPx = AXIS_LOCK_PX) => {
  if (Math.max(Math.abs(dx), Math.abs(dy)) < lockPx) return null;
  return Math.abs(dx) > Math.abs(dy) ? "x" : "y";
};

// Clamp the dragged offset between fully closed (0) and fully open (−reveal).
export const clampOffset = (baseOffset, dx, revealWidth) =>
  Math.max(-revealWidth, Math.min(0, baseOffset + dx));

// Half-threshold snap: settle open past half the reveal width, else shut.
export const settleOffset = (offset, revealWidth) =>
  offset <= -revealWidth / 2 ? -revealWidth : 0;

const SwipeActionRow = ({
  actions = [],
  actionWidth = DEFAULT_ACTION_WIDTH,
  disabled = false,
  style,
  contentStyle,
  children,
}) => {
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const touchRef = useRef(null); // { x, y, base, axis } for the active touch
  const suppressClickRef = useRef(false);

  const revealWidth = actions.length * actionWidth;
  const open = revealWidth > 0 && offset === -revealWidth;

  if (disabled || actions.length === 0) {
    return <div style={style}>{children}</div>;
  }

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, base: offset, axis: null };
    suppressClickRef.current = false;
    setDragging(true);
  };

  const onTouchMove = (e) => {
    const s = touchRef.current;
    if (!s) return;
    const t = e.touches[0];
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (s.axis == null) s.axis = resolveAxis(dx, dy);
    if (s.axis === "y") {
      // Vertical intent: this touch is a scroll, not a swipe — give it up
      // entirely (the row never moved; only x-locked touches translate it).
      touchRef.current = null;
      setDragging(false);
      return;
    }
    if (s.axis === "x") {
      suppressClickRef.current = true;
      setOffset(clampOffset(s.base, dx, revealWidth));
    }
  };

  const onTouchEnd = () => {
    const s = touchRef.current;
    touchRef.current = null;
    setDragging(false);
    if (s && s.axis === "x") setOffset((x) => settleOffset(x, revealWidth));
  };

  // Swallow the click synthesized after a horizontal drag, and turn a tap on
  // the open foreground into "close" instead of the row's own action.
  const onClickCapture = (e) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (offset !== 0) {
      e.preventDefault();
      e.stopPropagation();
      setOffset(0);
    }
  };

  const fireAction = (action) => (e) => {
    e.stopPropagation();
    setOffset(0);
    if (action.onAction) action.onAction();
  };

  return (
    <div
      data-testid="swipe-action-row"
      style={{ position: "relative", overflow: "hidden", ...style }}
    >
      <div
        aria-hidden={!open}
        style={{ position: "absolute", inset: 0, display: "flex", justifyContent: "flex-end" }}
      >
        {actions.map((action) => (
          <button
            key={action.key || action.label}
            type="button"
            tabIndex={open ? 0 : -1}
            onClick={fireAction(action)}
            aria-label={action.ariaLabel || undefined}
            style={{
              width: actionWidth,
              minWidth: Math.max(actionWidth, 44),
              minHeight: 44,
              border: "none",
              padding: 0,
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              background: action.background || (action.destructive ? "#DC2626" : "#2283FF"),
              color: "#FFFFFF",
              fontSize: 11.5,
              fontWeight: 700,
              fontFamily: "inherit",
            }}
          >
            {action.icon || null}
            {action.label}
          </button>
        ))}
      </div>
      <div
        data-testid="swipe-action-row-content"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        onClickCapture={onClickCapture}
        style={{
          position: "relative",
          background: "#FFFFFF",
          touchAction: "pan-y",
          transform: offset ? `translateX(${offset}px)` : undefined,
          transition: dragging ? undefined : "transform .18s",
          boxShadow: offset ? "1px 0 0 #E5E7EB" : undefined,
          ...contentStyle,
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default SwipeActionRow;
